import { Controller } from "@hotwired/stimulus";
import { validateFiles } from "./file_validation";
export default class extends Controller {
  static targets = ["file", "template", "submit", "list", "generalError", "wrapper", "button", "loading", "uploadLabel", "uploadSubLabel", "completeButton"];

  connect() {
    this.initDragAndDrop();
    this.addEventListeners();
    this.setConstraints();
    this.acceptedFormats = this.getAcceptedFormats();
    this.filesArray = [];
    this.allFilesValid = false;
  }

  setConstraints() {
    const { dataset } = this.submitTarget;
    this.maxFiles = parseInt(dataset.maxFiles) || 15;
    this.maxTotalSize = parseInt(dataset.maxTotalSize) || null;
    this.maxFileSize = parseInt(dataset.maxFileSize) || 15 * 1024 * 1024;
    this.minWidth = parseInt(dataset.minWidth) || null;
    this.minHeight = parseInt(dataset.minHeight) || null;
    this.saveOnUpload = dataset.saveOnUpload === "true";
  }

  getAcceptedFormats() {
    const accept = this.fileTarget.getAttribute("accept");
    return accept ? accept.split(",").map((format) => format.trim()) : [];
  }

  addEventListeners() {
    if (this.submitTarget && this.hasButtonTarget) {
      const formElement = this.submitTarget.closest("form");
      if (formElement) {
        formElement.addEventListener("submit", (event) => {
          event.preventDefault();
          this.toggleButtonLoading();

          const formData = new FormData(formElement);
          const actionUrl = formElement.getAttribute("action");

          fetch(actionUrl, {
            method: "POST",
            body: formData,
          })
            .then((response) => {
              if (response.ok) {
                this.handleSuccess();
              }
            })
            .catch((error) => {
              console.error("Error:", error);
            });
        });
      }
    }
  }

  handleSuccess() {
    this.wrapperTarget.classList.add("success");
    this.uploadLabelTarget.textContent = "File(s) submitted successfully";
    this.uploadSubLabelTarget.textContent =
      "Thank you for your submission. You may now close this window.";
    this.completeButtonTarget.classList.remove("hidden");
    this.buttonTarget.classList.add("hidden");
  }

  toggleButtonLoading() {
    const spinnerElement = this.buttonTarget.querySelector("[name='spinner']");
    const buttonTextElement = this.buttonTarget.querySelector("[name='button-text']");
    const currentlyUploading = buttonTextElement.textContent === "Uploading";

    const trashButtons = Array.from(this.listTarget.querySelectorAll(".trash-button"));
    trashButtons.forEach((button) => button.classList.toggle("hidden"));

    this.buttonTarget.disabled = !this.buttonTarget.disabled;

    if (spinnerElement) {
      spinnerElement.classList.toggle("hidden", currentlyUploading);
    }
    buttonTextElement.textContent = currentlyUploading ? "Upload" : "Uploading";
  }

  handleErrorEvent(event) {
    this.wrapperTarget.classList.add("error");
    this.errorTarget.textContent = event.detail.message;
  }

  initDragAndDrop() {
    const dragAndDropEvents = ["dragenter", "dragover", "dragleave", "drop"];
    const dragEvents = ["dragenter", "dragover"];
    const dropEvents = ["dragleave", "drop"];

    dragAndDropEvents.forEach((eventName) => {
      this.wrapperTarget.addEventListener(eventName, this.preventDefaults.bind(this));
    });

    dragEvents.forEach((eventName) => {
      this.wrapperTarget.addEventListener(eventName, this.highlight.bind(this));
    });

    dropEvents.forEach((eventName) => {
      this.wrapperTarget.addEventListener(eventName, this.unhighlight.bind(this));
    });

    this.wrapperTarget.addEventListener("drop", this.handleDrop.bind(this));
  }

  preventDefaults(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  async validateFiles() {
    const { fileErrors, generalError } = await validateFiles(
      this.fileTarget.files,
      this.maxFiles,
      this.maxTotalSize,
      this.maxFileSize,
      this.acceptedFormats,
      this.minWidth,
      this.minHeight
    );

    this.handleErrors(generalError, fileErrors);
  }

  handleErrors(generalError, fileErrors) {
    if (generalError || fileErrors.length > 0) {
      if (generalError) {
        this.wrapperTarget.classList.add("error");
        this.generalErrorTarget.textContent = generalError;
      }

      if (fileErrors.length > 0) {
        fileErrors.forEach((error) => {
          Array.from(this.listTarget.children).find((item) => {
            const fileToError = this.getFileIdentifier(item);
            if (fileToError.startsWith(error.file.name)) {
              let fileError = item.querySelector(".file-error");

              fileError.textContent = error.reason;
              fileError.classList.remove("hidden");

              this.updateFileInput(fileToError);
            }
          });
        });
      }

      this.allFilesValid = false;
    } else {
      this.wrapperTarget.classList.remove("error");
      this.allFilesValid = true;
    }
  }

  toggleUploadButton() {
    if (this.allFilesValid && this.hasButtonTarget && this.filesArray.length > 0) {
      this.buttonTarget.disabled = false;
    } else if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true;
    }
  }

  getFileIdentifier(file) {
    let fileName = file.querySelector(".file-name").textContent;
    fileName = fileName.replace(/\.\.\.$/, "");
    return fileName;
  }

  highlight() {
    this.wrapperTarget.classList.add("dragover");
  }

  unhighlight() {
    this.wrapperTarget.classList.remove("dragover");
  }

  async handleDrop(event) {
    const dataTransferFiles = event.dataTransfer.files;
    this.fileTarget.files = dataTransferFiles;
    await this.updateFileList();
  }

  emptyFileList() {
    const fileItems = Array.from(this.listTarget.children);
    const nonTemplateItems = fileItems.filter((item) => item.classList.contains("file-item"));

    nonTemplateItems.forEach((item) => item.remove());
  }

  createFileItemElement(file) {
    const clone = this.templateTarget.cloneNode(true);

    clone.classList.remove("file-item-template");
    clone.classList.add("file-item");
    clone.style.display = "flex";

    clone.querySelector(".file-name").textContent =
      file.name.length > 100 ? file.name.substring(0, 97) + "..." : file.name;
    clone.querySelector(".file-size").textContent = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;

    const trashButton = clone.querySelector(".trash-button");
    if (trashButton) {
      trashButton.dataset.fileId = file.name;
    }

    return clone;
  }

  removeAllFiles() {
    this.filesArray = [];
    this.fileTarget.value = "";
    this.emptyFileList();
    this.listTarget.classList.add("hidden");
    this.validateFiles();
  }

  updateFileInput(fileNameToRemove) {
    const filesArray = Array.from(this.fileTarget.files);
    const updatedFilesArray = filesArray.filter((file) => !file.name.startsWith(fileNameToRemove));
    const dataTransfer = new DataTransfer();

    updatedFilesArray.forEach((file) => dataTransfer.items.add(file));
    this.fileTarget.files = dataTransfer.files;

    this.filesArray = updatedFilesArray;
  }
}
